import { useCallback } from 'react';

import { FetchDataArgs, SortDirection } from 'components/tables/data-table-context';
import { useResponse } from 'contexts/response';
import { Page } from 'types';

type FetchPaginatedArgs = Omit<FetchDataArgs, 'sortDirection'> & { sortDirection?: SortDirection | 'asc' | 'desc' };

export const useFetch = <T>(endpoint: string) => {
  const { handleError } = useResponse();

  const fetchCsv = useCallback(
    async (
      columns: string[],
      filterString?: string,
      sortField?: string,
      sortDirection?: string,
      headers?: string[]
    ) => {
      const url: URL = new URL(`/api/${endpoint}/csv`, window.location.origin);

      if (columns) url.searchParams.append('columns', columns.join(','));
      if (filterString) url.searchParams.append('filterString', filterString);
      if (headers) url.searchParams.append('headers', headers.join(','));
      if (sortDirection) url.searchParams.append('sortDirection', sortDirection);
      if (sortField) url.searchParams.append('sortField', sortField);
      url.searchParams.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);

      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: new Headers({ Accept: 'text/csv', Authorization: `Bearer ${localStorage.getItem('token')}` })
        });

        if (!response.ok) {
          handleError(response);
          return;
        }

        const blob = await response.blob();

        const filename = response.headers
          .get('Content-Disposition')
          ?.split(';')
          .find(h => h.includes('filename'))
          ?.split('=')
          .at(1)
          ?.replace(/"/g, '');

        const fileUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = filename ?? endpoint;
        link.click();
        link.remove();
      } catch (e) {
        handleError(e);
      }
    },
    [handleError]
  );

  const fetchPaginated = useCallback(
    async ({ filterString, pageNum, pageSize, sortDirection, sortField }: FetchPaginatedArgs): Promise<Page<T>> => {
      const headers: HeadersInit = new Headers();
      const token: string | null = localStorage.getItem('token');
      const url: URL = new URL(`/api/${endpoint}`, window.location.origin);

      if (filterString) url.searchParams.append('filterString', filterString);
      if (typeof pageNum === 'number') url.searchParams.append('pageNum', `${pageNum}`);
      if (pageSize) url.searchParams.append('pageSize', `${pageSize}`);
      if (sortDirection) url.searchParams.append('sortDirection', sortDirection);
      if (sortField) url.searchParams.append('sortField', sortField);
      if (token) headers.set('Authorization', `Bearer ${token}`);

      try {
        const response = await fetch(url, { method: 'GET', headers });
        if (response.ok) {
          const page = await response.json();
          if (typeof page === 'object' && 'content' in page && Array.isArray(page.content)) {
            return page;
          }
        }
      } catch (e) {
        console.error(e);
      }

      return { content: [] };
    },
    [endpoint]
  );

  return { fetchCsv, fetchPaginated };
};
